import type { CelestialBody } from '@/_types/celestial-body'

/**
 * Every celestial body in the QuickCasa Universe, in orbital order. The first
 * entry (tier `sun`) is the central QuickCasa star; the rest orbit it. The final
 * entry is a hidden anomaly that only reveals itself through an easter egg.
 *
 * This array is the single source of truth for the whole experience - the scene,
 * the info cards, the particle trails, and navigation all read from it. To add a
 * service, drop in one more entry; everything downstream adapts automatically.
 */
export const celestialBodies: readonly CelestialBody[] = [
  {
    id: 'quickcasa',
    name: 'QuickCasa',
    url: 'https://quickcasa.ai',
    domain: 'quickcasa.ai',
    description: 'The AI leasing platform at the centre of everything.',
    tier: 'sun',
    visualStyle: 'sun',
    radius: 6,
    orbitRadius: 0,
    orbitSpeed: 0,
    orbitInclination: 0,
    startAngle: 0,
    spinSpeed: 0.04,
    accentColour: '#FF8A3D',
    glowColour: '#FFE07A',
    surfaceColour: '#FF6B2C',
    noiseScale: 2.4,
    ring: null,
    isHidden: false,
    connections: [],
  },
  {
    id: 'api',
    name: 'API',
    url: 'https://api.quickcasa.ai',
    domain: 'api.quickcasa.ai',
    description: 'The backend brain powering the entire ecosystem.',
    tier: 'inner',
    visualStyle: 'circuit',
    radius: 1.7,
    orbitRadius: 18,
    orbitSpeed: 0.125,
    orbitInclination: 0.08,
    startAngle: 0.4,
    spinSpeed: 0.32,
    accentColour: '#00D4FF',
    glowColour: '#2BE8C0',
    surfaceColour: '#16323A',
    noiseScale: 3.2,
    ring: null,
    isHidden: false,
    connections: ['admin-portal', 'llm-api', 'media-cdn'],
  },
  {
    id: 'admin-portal',
    name: 'Admin Portal',
    url: 'https://clients.quickcasa.ai',
    domain: 'clients.quickcasa.ai',
    description: 'Where property teams manage leads, properties, and everything else.',
    tier: 'inner',
    visualStyle: 'hologram',
    radius: 2.1,
    orbitRadius: 27,
    orbitSpeed: 0.1,
    orbitInclination: -0.12,
    startAngle: 2.6,
    spinSpeed: 0.26,
    accentColour: '#6C63FF',
    glowColour: '#A78BFA',
    surfaceColour: '#2A2350',
    noiseScale: 2.6,
    ring: null,
    isHidden: false,
    connections: [],
  },
  {
    id: 'website-preview',
    name: 'Website Builder Preview',
    url: 'https://preview.quickcasa.ai',
    domain: 'preview.quickcasa.ai',
    description: 'A live, see-through look at websites as they are being built.',
    tier: 'mid',
    visualStyle: 'crystal',
    radius: 1.8,
    orbitRadius: 38,
    orbitSpeed: 0.072,
    orbitInclination: 0.16,
    startAngle: 4.1,
    spinSpeed: 0.22,
    accentColour: '#8FE3FF',
    glowColour: '#CFF6FF',
    surfaceColour: '#3A6E8C',
    noiseScale: 2,
    ring: {
      innerScale: 1.6,
      outerScale: 2.4,
      colour: '#CFF6FF',
      opacity: 0.35,
      tilt: 0.5,
    },
    isHidden: false,
    connections: ['forms', 'micro-sites'],
  },
  {
    id: 'forms',
    name: 'Forms',
    url: 'https://forms.quickcasa.ai',
    domain: 'forms.quickcasa.ai',
    description: 'Self-hosted forms that unfold into single or multi-page flows.',
    tier: 'mid',
    visualStyle: 'origami',
    radius: 1.5,
    orbitRadius: 48,
    orbitSpeed: 0.06,
    orbitInclination: -0.1,
    startAngle: 0.9,
    spinSpeed: 0.3,
    accentColour: '#FFB347',
    glowColour: '#FFE3A8',
    surfaceColour: '#C9A24B',
    noiseScale: 4,
    ring: null,
    isHidden: false,
    connections: [],
  },
  {
    id: 'micro-sites',
    name: 'Micro-Sites',
    url: 'https://micro.qc-l.ink',
    domain: 'micro.qc-l.ink',
    description: 'Linktree-style sites for properties and agents.',
    tier: 'mid',
    visualStyle: 'asteroid-belt',
    radius: 1.3,
    orbitRadius: 58,
    orbitSpeed: 0.05,
    orbitInclination: 0.2,
    startAngle: 3.3,
    spinSpeed: 0.18,
    accentColour: '#B388FF',
    glowColour: '#D6C2FF',
    surfaceColour: '#5A4A7A',
    noiseScale: 3,
    ring: null,
    isHidden: false,
    connections: [],
  },
  {
    id: 'video-player',
    name: 'Video Player',
    url: 'https://video.quickcasa.ai',
    domain: 'video.quickcasa.ai',
    description: 'A lightweight, embeddable cinematic video player.',
    tier: 'mid',
    visualStyle: 'cinema',
    radius: 1.9,
    orbitRadius: 68,
    orbitSpeed: 0.044,
    orbitInclination: -0.18,
    startAngle: 5.5,
    spinSpeed: 0.2,
    accentColour: '#FF6B6B',
    glowColour: '#FF9E9E',
    surfaceColour: '#5A1E22',
    noiseScale: 2.2,
    ring: null,
    isHidden: false,
    connections: [],
  },
  {
    id: 'media-cdn',
    name: 'Media CDN',
    url: 'https://media.quickcasa.ai',
    domain: 'media.quickcasa.ai',
    description: 'Authenticated media delivery across the globe.',
    tier: 'outer',
    visualStyle: 'satellite',
    radius: 1.6,
    orbitRadius: 82,
    orbitSpeed: 0.034,
    orbitInclination: 0.14,
    startAngle: 1.7,
    spinSpeed: 0.4,
    accentColour: '#00D4FF',
    glowColour: '#7DEBFF',
    surfaceColour: '#123244',
    noiseScale: 2.8,
    ring: {
      innerScale: 1.5,
      outerScale: 3,
      colour: '#7DEBFF',
      opacity: 0.4,
      tilt: 0.32,
    },
    isHidden: false,
    connections: ['assets'],
  },
  {
    id: 'assets',
    name: 'Assets',
    url: 'https://assets.quickcasa.ai',
    domain: 'assets.quickcasa.ai',
    description: 'Fast, static asset hosting for the whole platform.',
    tier: 'outer',
    visualStyle: 'cargo',
    radius: 1.7,
    orbitRadius: 94,
    orbitSpeed: 0.029,
    orbitInclination: -0.22,
    startAngle: 4.8,
    spinSpeed: 0.16,
    accentColour: '#FFA552',
    glowColour: '#FFD0A0',
    surfaceColour: '#46352A',
    noiseScale: 3.4,
    ring: null,
    isHidden: false,
    connections: [],
  },
  {
    id: 'llm-api',
    name: 'LLM API',
    url: 'https://llm.quickcasa.ai',
    domain: 'llm.quickcasa.ai',
    description: 'A unified AI API for text, image, and video generation.',
    tier: 'outer',
    visualStyle: 'brain',
    radius: 2.4,
    orbitRadius: 106,
    orbitSpeed: 0.025,
    orbitInclination: 0.1,
    startAngle: 2.1,
    spinSpeed: 0.14,
    accentColour: '#FF4DD2',
    glowColour: '#FFA6EC',
    surfaceColour: '#3A123A',
    noiseScale: 2.6,
    ring: null,
    isHidden: false,
    connections: ['mcp-server'],
  },
  {
    id: 'mcp-server',
    name: 'MCP Server',
    url: 'https://mcp.quickcasa.ai',
    domain: 'mcp.quickcasa.ai',
    description: 'AI-powered website building from Claude Code and Cursor.',
    tier: 'outer',
    visualStyle: 'mech',
    radius: 1.8,
    orbitRadius: 118,
    orbitSpeed: 0.022,
    orbitInclination: -0.08,
    startAngle: 5.9,
    spinSpeed: 0.36,
    accentColour: '#59F2B0',
    glowColour: '#B6FFE0',
    surfaceColour: '#1E3A30',
    noiseScale: 3,
    ring: null,
    isHidden: false,
    connections: [],
  },
  {
    id: 'developer-portal',
    name: 'Developer Portal',
    url: 'https://developer.quickcasa.ai',
    domain: 'developer.quickcasa.ai',
    description: 'Developer docs and resources for the QuickCasa platform.',
    tier: 'distant',
    visualStyle: 'station',
    radius: 1.6,
    orbitRadius: 136,
    orbitSpeed: 0.017,
    orbitInclination: 0.24,
    startAngle: 0.2,
    spinSpeed: 0.24,
    accentColour: '#5C9CFF',
    glowColour: '#B0CCFF',
    surfaceColour: '#1A2A52',
    noiseScale: 2.4,
    ring: {
      innerScale: 1.7,
      outerScale: 2.8,
      colour: '#B0CCFF',
      opacity: 0.3,
      tilt: -0.4,
    },
    isHidden: false,
    connections: [],
  },
  {
    id: 'quickpdf',
    name: 'QuickPDF',
    url: 'https://quickpdf.ca',
    domain: 'quickpdf.ca',
    description: 'A frictionless PDF form-filling tool.',
    tier: 'distant',
    visualStyle: 'parchment',
    radius: 1.5,
    orbitRadius: 150,
    orbitSpeed: 0.014,
    orbitInclination: -0.16,
    startAngle: 3.7,
    spinSpeed: 0.12,
    accentColour: '#E8C77A',
    glowColour: '#FFE7B8',
    surfaceColour: '#6B5A38',
    noiseScale: 3.6,
    ring: null,
    isHidden: false,
    connections: [],
  },
  {
    id: 'anomaly',
    name: 'The Anomaly',
    url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
    domain: 'unknown signal',
    description: 'An uncharted body emitting a very persuasive transmission.',
    tier: 'distant',
    visualStyle: 'brain',
    radius: 1.2,
    orbitRadius: 196,
    orbitSpeed: 0.009,
    orbitInclination: 0.5,
    startAngle: 1.1,
    spinSpeed: 0.5,
    accentColour: '#FF00AA',
    glowColour: '#FF66CC',
    surfaceColour: '#220022',
    noiseScale: 4.5,
    ring: null,
    isHidden: true,
    connections: [],
  },
]
